// src/pages/invoices/components/InvoiceActions.js
import React from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  Block,
  ReceiptLong,
  PriceCheck
} from '@mui/icons-material';
// Importar hook y componente con rutas ajustadas
import { useRoleAccess } from '../../../hooks/useRoleAccess'; // VERIFICA ESTA RUTA
import ActionButton from '../../../components/ActionButton'; // VERIFICA ESTA RUTA

/**
 * Componente para mostrar acciones comunes de facturas (ver, editar, eliminar)
 * con control de acceso basado en roles y estilo de íconos blanco.
 */
export const InvoiceActions = ({
  invoice,
  onPreview,
  onEdit,
  onDelete,
  onAnular,
  onNota,
  onRetencion,
  onDownload, // Mantenemos el prop aunque no lo usemos para evitar errores
  ...props
}) => {
  // Obtener funciones de permiso del hook
  const { canEdit, canDelete } = useRoleAccess();

  // Un borrador se elimina; una factura emitida se anula. Ofrecer «Eliminar» en
  // una emitida era ofrecer un botón que el servidor siempre rechaza: el usuario
  // se quedaba sin salida, porque la anulación no estaba en la pantalla.
  const emitida = invoice?.status && invoice.status !== 'draft';
  const anulada = invoice?.status === 'void';

  return (
    <Box sx={{ display: 'flex', gap: '4px', ...props.sx }}>
      {/* Botón Ver (ya debería ser blanco por defecto) */}
      {onPreview && (
        <Tooltip title="Previsualizar">
          <IconButton
            onClick={() => onPreview(invoice)}
            size="small"
          >
            <Visibility fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}

      {/* El botón de descargar PDF ha sido eliminado */}

      {/* Botón Editar: sólo mientras sea borrador. Una emitida es inalterable */}
      {onEdit && !emitida && (
        <ActionButton
          type="edit" // Verifica canEdit()
          onClick={() => onEdit(invoice)}
          tooltipTitle="Editar Factura"
          isIconButton={true}
          showDisabled={true}
          buttonProps={{
            size: 'small',
          }}
        >
          <Edit fontSize="inherit" /> {/* Ícono directo */}
        </ActionButton>
      )}

      {/* Botón Eliminar: sólo para borradores */}
      {onDelete && !emitida && (
        <ActionButton
          type="delete" // Verifica canDelete()
          onClick={() => onDelete(invoice._id)}
          tooltipTitle="Eliminar borrador"
          isIconButton={true}
          showDisabled={true}
          buttonProps={{
            size: 'small',
          }}
        >
           <Delete fontSize="inherit" /> {/* Ícono directo */}
        </ActionButton>
      )}

      {/* Nota de crédito o débito: sólo sobre una factura vigente. Sin esto, una
          devolución parcial obligaba a anular la factura entera y rehacerla */}
      {onNota && emitida && !anulada && (
        <ActionButton
          type="edit"
          onClick={() => onNota(invoice)}
          tooltipTitle="Emitir nota de crédito o débito"
          isIconButton={true}
          showDisabled={true}
          buttonProps={{ size: 'small' }}
        >
          <ReceiptLong fontSize="inherit" />
        </ActionButton>
      )}

      {/* Retención de IVA: sólo sobre una factura vigente y ya emitida. El
          comprobante llega después de facturar, a veces semanas más tarde, así
          que no puede cargarse en el formulario de la factura. El icono queda
          resaltado cuando la factura ya tiene una registrada. */}
      {onRetencion && emitida && !anulada && (
        <Tooltip title={invoice?.ivaRetenido > 0
          ? `Retención registrada: ${Number(invoice.ivaRetenido).toFixed(2)}`
          : 'Registrar retención de IVA'}>
          <IconButton
            onClick={() => onRetencion(invoice)}
            size="small"
            sx={{ color: invoice?.ivaRetenido > 0 ? '#F5A623' : undefined }}
          >
            <PriceCheck fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}

      {/* Botón Anular: para las emitidas que todavía están vigentes */}
      {onAnular && emitida && !anulada && (
        <ActionButton
          type="delete" // Mismo permiso que eliminar
          onClick={() => onAnular(invoice)}
          tooltipTitle="Anular factura"
          isIconButton={true}
          showDisabled={true}
          buttonProps={{
            size: 'small',
          }}
        >
          <Block fontSize="inherit" />
        </ActionButton>
      )}
    </Box>
  );
};

export default InvoiceActions;